import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import Honeypot from "@/components/Honeypot";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { Disc3, Check, Loader2, Star, ArrowRight, ExternalLink } from "lucide-react";

const inputCls =
  "w-full bg-ink border border-stroke px-4 py-3 font-mono text-sm text-paper placeholder:text-muted-gray focus:outline-none focus:border-acid transition-colors";

export default function Sponsorship() {
  const [tiers, setTiers] = useState([]);
  const [form, setForm] = useState({ name: "", brand: "", email: "", tier: "", message: "" });
  const [website, setWebsite] = useState(""); // honeypot
  const [loadedAtMs] = useState(() => Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { pay_url, amount } on success

  useEffect(() => {
    api.get("/sponsorship/tiers").then((r) => setTiers(r.data)).catch(() => setTiers([]));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pick = (tierName) => {
    set("tier", tierName);
    document.getElementById("sponsor-form")?.scrollIntoView({ behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/sponsorship/inquiries", { ...form, website, loaded_at_ms: loadedAtMs });
      setDone({ pay_url: data.pay_url, amount: data.amount });
      toast.success("Inquiry sent — we'll be in touch");
      setForm({ name: "", brand: "", email: "", tier: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="relative z-20 flex items-center justify-between px-6 lg:px-12 py-5 border-b border-stroke bg-surface/70 backdrop-blur-sm">
        <Link to="/" className="flex items-center gap-2">
          <Disc3 className="text-acid" size={28} />
          <span className="font-display text-3xl text-paper">POSTERVAULT</span>
        </Link>
        <Link to="/register" className="font-mono text-xs uppercase tracking-wider bg-acid text-ink font-bold px-4 py-2.5 hard-shadow">
          Start Collecting
        </Link>
      </header>

      <section className="relative z-10 px-6 lg:px-12 py-16 flex-1">
        <div className="max-w-6xl mx-auto">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-acid mb-4">Partner With Us · <Link to="/advertise" className="text-paper hover:text-acid underline">Banner Advertising →</Link></div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-paper leading-[0.9] ink-offset">
            SPONSORSHIP<br />TIERS
          </h1>
          <p className="mt-5 font-sans text-base text-muted-gray max-w-xl leading-relaxed">
            Put your gallery, print shop or brand in front of serious poster collectors.
            Pick a tier and we'll get you featured across PosterVault.
          </p>

          <div className="grid md:grid-cols-3 gap-5 mt-12" data-testid="sponsor-tiers">
            {tiers.map((t) => (
              <div
                key={t.id}
                data-testid={`sponsor-tier-${t.id}`}
                className={`relative bg-surface border p-7 hard-shadow flex flex-col ${
                  t.popular ? "border-acid" : "border-stroke"
                }`}
              >
                {t.popular && (
                  <span className="absolute -top-3 left-7 font-mono text-[10px] uppercase tracking-widest bg-acid text-ink font-bold px-2.5 py-1 flex items-center gap-1">
                    <Star size={11} /> Most Popular
                  </span>
                )}
                <div className="font-mono text-[11px] uppercase tracking-widest text-muted-gray">{t.tagline}</div>
                <div className="font-display text-4xl text-paper mt-1">{t.name}</div>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-display text-5xl text-acid">${t.price}</span>
                  <span className="font-mono text-xs text-muted-gray">/ {t.period}</span>
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 font-sans text-sm text-paper/90">
                      <Check size={16} className="text-acid shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => pick(t.name)}
                  data-testid={`sponsor-select-${t.id}`}
                  className={`mt-7 w-full font-mono text-xs uppercase tracking-wider py-3.5 font-bold flex items-center justify-center gap-2 ${
                    t.popular ? "bg-acid text-ink hover:bg-glow" : "border border-stroke text-paper hover:border-acid hover:text-acid"
                  }`}
                >
                  Choose {t.name} <ArrowRight size={15} />
                </button>
              </div>
            ))}
          </div>

          {/* Inquiry form */}
          <div id="sponsor-form" className="mt-16 max-w-2xl">
            <h2 className="font-display text-4xl text-paper">Become a Sponsor</h2>
            <p className="font-mono text-xs text-muted-gray mt-2 mb-6">
              Tell us about your brand and we'll set you up.
            </p>

            {done ? (
              <div className="bg-surface border border-acid/40 p-8 text-center" data-testid="sponsor-success">
                <Check className="text-acid mx-auto mb-3" size={32} />
                <div className="font-display text-3xl text-paper">Thanks — inquiry received!</div>
                <p className="font-mono text-xs text-muted-gray mt-2">
                  {done.pay_url ? "Complete payment below and we'll activate your sponsorship." : "Our team will reach out shortly."}
                </p>
                {done.pay_url && (
                  <a href={done.pay_url} target="_blank" rel="noopener noreferrer" data-testid="sponsor-pay-link"
                    className="mt-5 inline-flex items-center gap-2 font-mono text-sm uppercase tracking-wider bg-acid text-ink font-bold px-6 py-3.5 hover:bg-glow">
                    <ExternalLink size={16} /> Pay {done.amount ? `$${done.amount}` : "via PayPal"} Now
                  </a>
                )}
                <div className="mt-5">
                  <button onClick={() => setDone(null)} className="font-mono text-xs uppercase text-muted-gray hover:text-acid hover:underline">
                    Send another
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-surface/85 backdrop-blur-md border border-stroke p-7 space-y-4" data-testid="sponsor-form">
                <Honeypot value={website} onChange={setWebsite} />
                <div className="grid sm:grid-cols-2 gap-4">
                  <input data-testid="sponsor-name-input" required placeholder="YOUR NAME" className={inputCls}
                    value={form.name} onChange={(e) => set("name", e.target.value)} />
                  <input data-testid="sponsor-brand-input" placeholder="BRAND / COMPANY" className={inputCls}
                    value={form.brand} onChange={(e) => set("brand", e.target.value)} />
                </div>
                <input data-testid="sponsor-email-input" type="email" required placeholder="EMAIL" className={inputCls}
                  value={form.email} onChange={(e) => set("email", e.target.value)} />
                <select data-testid="sponsor-tier-select" className={inputCls}
                  value={form.tier} onChange={(e) => set("tier", e.target.value)}>
                  <option value="">Select a tier (optional)</option>
                  {tiers.map((t) => <option key={t.id} value={t.name}>{t.name} — ${t.price}/{t.period}</option>)}
                </select>
                <textarea data-testid="sponsor-message-input" placeholder="Tell us about your brand..."
                  className={`${inputCls} min-h-[110px] resize-y`}
                  value={form.message} onChange={(e) => set("message", e.target.value)} />
                <button type="submit" disabled={submitting} data-testid="sponsor-submit-button"
                  className="w-full bg-acid text-ink font-bold font-mono text-sm uppercase tracking-wider py-3.5 hover:bg-glow disabled:opacity-50 flex items-center justify-center gap-2">
                  {submitting && <Loader2 size={16} className="animate-spin" />} Submit Inquiry
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
